import React, { useEffect, useRef } from 'react';

/* Animated delivery route with moving truck dot and package pulse */
export const AnimatedRouteSection = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    let t = 0;

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio, 1.5);
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = rect.width + 'px';
      canvas.style.height = rect.height + 'px';
      ctx.scale(dpr, dpr);
    };
    resize();
    window.addEventListener('resize', resize);

    const W = () => canvas.parentElement.getBoundingClientRect().width;
    const H = () => canvas.parentElement.getBoundingClientRect().height;

    /* Waypoints for a delivery route across India */
    const getRoute = () => {
      const w = W(), h = H();
      return [
        { x: w * 0.08, y: h * 0.7, label: 'Mumbai' },
        { x: w * 0.22, y: h * 0.45, label: 'Ahmedabad' },
        { x: w * 0.38, y: h * 0.25, label: 'Delhi' },
        { x: w * 0.55, y: h * 0.55, label: 'Kolkata' },
        { x: w * 0.72, y: h * 0.35, label: 'Bangalore' },
        { x: w * 0.92, y: h * 0.6, label: 'Chennai' },
      ];
    };

    const animate = () => {
      t += 0.003;
      const w = W(), h = H();
      ctx.clearRect(0, 0, w, h);
      const route = getRoute();

      /* Draw connecting route path */
      ctx.setLineDash([8, 6]);
      ctx.lineDashOffset = -t * 800;
      ctx.strokeStyle = 'rgba(245, 158, 11, 0.35)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      route.forEach((p, i) => {
        if (i === 0) ctx.moveTo(p.x, p.y);
        else {
          const prev = route[i-1];
          const cpX = (prev.x + p.x) / 2;
          const cpY = Math.min(prev.y, p.y) - 30;
          ctx.quadraticCurveTo(cpX, cpY, p.x, p.y);
        }
      });
      ctx.stroke();
      ctx.setLineDash([]);

      /* Draw city nodes */
      route.forEach((p, i) => {
        const pulse = 1 + Math.sin(t * 60 + i * 1.5) * 0.3;

        // Glow ring
        ctx.strokeStyle = `rgba(245, 158, 11, ${0.2 / pulse})`;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 18 * pulse, 0, Math.PI * 2);
        ctx.stroke();

        // Outer glow
        const glow = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, 20);
        glow.addColorStop(0, 'rgba(245, 158, 11, 0.2)');
        glow.addColorStop(1, 'transparent');
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 20, 0, Math.PI * 2);
        ctx.fill();

        // Dot
        ctx.fillStyle = '#f59e0b';
        ctx.shadowColor = '#f59e0b';
        ctx.shadowBlur = 12;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 5, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;

        // Label
        ctx.fillStyle = 'rgba(255,255,255, 0.8)';
        ctx.font = '600 11px system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(p.label, p.x, p.y + 28);
      });

      /* Moving truck indicator along the route */
      const totalSegs = route.length - 1;
      const progress = (t * 8) % totalSegs;
      const segIdx = Math.floor(progress);
      const segFrac = progress - segIdx;
      if (segIdx < totalSegs) {
        const from = route[segIdx];
        const to = route[segIdx + 1];
        const cpX = (from.x + to.x) / 2;
        const cpY = Math.min(from.y, to.y) - 30;
        // Bezier interpolation
        const mt = 1 - segFrac;
        const tx = mt*mt*from.x + 2*mt*segFrac*cpX + segFrac*segFrac*to.x;
        const ty = mt*mt*from.y + 2*mt*segFrac*cpY + segFrac*segFrac*to.y;

        // Truck glow trail
        const trailGlow = ctx.createRadialGradient(tx, ty, 0, tx, ty, 25);
        trailGlow.addColorStop(0, 'rgba(59, 130, 246, 0.4)');
        trailGlow.addColorStop(1, 'transparent');
        ctx.fillStyle = trailGlow;
        ctx.beginPath();
        ctx.arc(tx, ty, 25, 0, Math.PI * 2);
        ctx.fill();

        // Truck dot
        ctx.fillStyle = '#3b82f6';
        ctx.shadowColor = '#3b82f6';
        ctx.shadowBlur = 15;
        ctx.beginPath();
        ctx.arc(tx, ty, 7, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;

        // Inner highlight
        ctx.fillStyle = '#93c5fd';
        ctx.beginPath();
        ctx.arc(tx, ty, 3, 0, Math.PI * 2);
        ctx.fill();
      }

      /* Floating particles in background */
      for (let i = 0; i < 20; i++) {
        const px = ((Math.sin(t * 10 + i * 7) + 1) / 2) * w;
        const py = ((Math.cos(t * 8 + i * 5) + 1) / 2) * h;
        ctx.fillStyle = `rgba(245, 158, 11, ${0.08 + Math.sin(t * 20 + i) * 0.05})`;
        ctx.beginPath();
        ctx.arc(px, py, 1.5, 0, Math.PI * 2);
        ctx.fill();
      }

      animId = requestAnimationFrame(animate);
    };

    animate();
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);

  return (
    <section className="relative py-16 bg-gradient-to-br from-slate-900 via-[#0a0f1e] to-slate-900 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-2 bg-amber-500/10 text-amber-500 rounded-full text-sm font-bold tracking-wider mb-4">LIVE ROUTES</span>
          <h2 className="font-outfit text-3xl lg:text-4xl font-black text-white">Real-Time Shipment Tracking</h2>
          <p className="text-slate-400 mt-2 max-w-lg mx-auto">Watch your cargo move across major Indian cities with precision tracking</p>
        </div>
        <div className="relative h-[220px] lg:h-[280px] w-full rounded-2xl overflow-hidden border border-white/5" data-testid="route-animation">
          <canvas ref={canvasRef} className="w-full h-full" />
        </div>
      </div>
    </section>
  );
};
